import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/capacitor";
import { init as sentryReactInit } from "@sentry/react";
import { sentryPrivacyOptions } from "@vidimo/shared/sentry-scrub";
import { useEffect } from "react";
import { env } from "@/lib/env";
import type { AppSession } from "@/lib/offline-session";
import { SUPPORT_SESSION } from "@/lib/support-session";

export function initSentry() {
  const dsn = env.VITE_SENTRY_DSN;
  if (!dsn) {
    return;
  }

  // @sentry/capacitor wraps @sentry/react: on web it ships events via the JS
  // transport; on iOS/Android it forwards to the native SDK (the SentryCapacitor
  // Swift package on iOS, the Gradle module cap sync wires on Android), which also
  // reports on channels these hooks never see: docs/observability-privacy.md,
  // "Native channels on the staff app". Single project, single DSN -- the
  // `platform` tag below distinguishes events in Sentry's UI.
  Sentry.init(
    {
      // The staff app renders guest passports / national IDs (the MRZ scan), so
      // guest PII and message content must never leave the device in an event or
      // breadcrumb. The scrubbers strip it; JS-added breadcrumbs are scrubbed at
      // creation, so they reach native already sanitised.
      ...sentryPrivacyOptions,
      // Metrics dispatch through `beforeSendMetric`, which none of the
      // scrubbers cover, and the client defaults them ON. The top-level
      // `enableMetrics` is not the control here: @sentry/capacitor drops that
      // key from its own options and forwards `_experiments.enableMetrics` to
      // the browser client in its place, so an unset value resolves to the
      // client's `?? true`. See docs/observability-privacy.md.
      _experiments: { enableMetrics: false },
      dsn,
      environment: env.VITE_SENTRY_ENVIRONMENT ?? "development",
      // No Session Replay: @sentry/capacitor 4.0.0 omits the web replay options
      // (`replaysSessionSampleRate` / `replaysOnErrorSampleRate`) from its config
      // by design, so the browser replay integration can't sample here. Adding
      // replay would mean native Mobile Session Replay (a separate native-SDK
      // config with on-device masking, not the web `sentry-block` mechanism), and
      // the passport/ID scanner must stay excluded. See
      // docs/observability-privacy.md.
      initialScope: {
        // A support tab signs in as one of the customer's staff, so its
        // events carry that staff id: this tells the agent's apart.
        tags: {
          platform: Capacitor.getPlatform(),
          support_session: SUPPORT_SESSION !== null,
        },
      },
      release: `superhost-mobile@${__APP_VERSION__}+${__GIT_COMMIT_SHORT__}`,
      tracesSampleRate: 0.1,
    },
    sentryReactInit
  );
}

/**
 * Ties events to the signed-in staff member and the property their session is
 * scoped to. The JS scope syncs both to the native SDKs, so crashes carry them
 * too. Only the ids: docs/observability-privacy.md, "Identity".
 */
export function useSentryIdentity(session: AppSession | null): void {
  const userId = session?.user.id;
  const propertyId = session?.session.activeOrganizationId ?? undefined;

  useEffect(() => {
    Sentry.setUser(userId ? { id: userId } : null);
  }, [userId]);

  useEffect(() => {
    Sentry.setTag("property", propertyId);
  }, [propertyId]);
}
